import {
  ArrayPath,
  Control,
  FieldArray,
  Path,
  useFieldArray,
} from 'react-hook-form';
import StyledButton from '../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledInput from '../styled/StyledInput';
import { Label } from '../ui/label';
import { ExtendedReceiverCompanyFormData } from '@/types/profile/ProfileFormData';
import { useTranslation } from 'react-i18next';

interface ReceiverReservationEmailsProps<
  FieldsType extends ExtendedReceiverCompanyFormData,
> {
  control: Control<FieldsType>;
  isViewMode?: boolean;
}

const ReceiverReservationEmails = <
  FieldsType extends ExtendedReceiverCompanyFormData,
>({
  control,
  isViewMode = false,
}: ReceiverReservationEmailsProps<FieldsType>) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'company.reservationEmails' as ArrayPath<FieldsType>,
  });

  return (
    <>
      {fields.map((field, index) => (
        <ReservationEmailForm
          key={field.id}
          index={index}
          control={control}
          onRemoveContact={() => remove(index)}
          disabled={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          title={t('pages.profile.reservationEmails.addEmail')}
          className="text-brand-600 w-fit p-0"
          variant="link"
          icon={<PlusIcon className="w-5 h-5 fill-brand-600" />}
          onClick={() =>
            append({
              email: undefined,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0])
          }
        />
      )}
    </>
  );
};

export default ReceiverReservationEmails;

const ReservationEmailForm = <
  FieldsType extends ExtendedReceiverCompanyFormData,
>({
  control,
  index,
  onRemoveContact,
  disabled = false,
}: {
  control: Control<FieldsType>;
  index: number;
  disabled?: boolean;
  onRemoveContact: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex justify-between">
        <Label className="text-gray-700 transition-all duration-200 ease-in-out">
          {t('pages.profile.reservationEmails.email', { index: index + 1 })}
          {index === 0 && t('pages.profile.reservationEmails.required')}
        </Label>
        {index > 0 && !disabled && (
          <StyledButton
            title={t('pages.profile.reservationEmails.removeEmail')}
            className="p-0 h-auto"
            variant="link"
            onClick={onRemoveContact}
          />
        )}
      </div>
      <StyledInput
        control={control}
        name={`company.reservationEmails.${index}.email` as Path<FieldsType>}
        type="email"
        placeholder={t('pages.profile.contacts.email')}
        disabled={disabled}
      />
    </>
  );
};
